import { useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#17B8A6] focus:border-[#17B8A6] transition-colors duration-150";

const ContactForm = ({ formLocation = "contact_page", compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, { ...form, form_location: formLocation, page_url: window.location.href });
      trackEvent("generate_lead", {
        form_name: "free_consultation",
        form_location: formLocation,
        page_location: window.location.href,
        value: 1,
        currency: "AUD",
      });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", business_name: "", message: "", website: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err.response?.status === 429
          ? "You've submitted a few times recently. Please wait a little while, or email us directly at maneesh@umi-it.com.au."
          : "Something went wrong sending your enquiry. Please try again, or email us directly at maneesh@umi-it.com.au."
      );
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-form-success-message" className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle2 size={40} className="mx-auto text-emerald-500" aria-hidden="true" />
        <h3 className="mt-4 text-xl font-semibold text-[#0B2545]">Thank you — your enquiry is on its way.</h3>
        <p className="mt-2 text-base text-slate-600">
          An Adelaide IT specialist will be in touch shortly. If it's urgent, call us on{" "}
          <a href="tel:+61489222864" className="text-[#0D9488] font-medium underline">0489 222 864</a> or email{" "}
          <a href="mailto:maneesh@umi-it.com.au" className="text-[#0D9488] font-medium underline">maneesh@umi-it.com.au</a>.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-5" noValidate={false}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700 mb-1.5">Full name *</label>
          <input id="contact-name" data-testid="contact-form-input-name" name="name" type="text" required maxLength={120}
            value={form.name} onChange={update} className={inputClass} placeholder="Jane Smith" autoComplete="name" />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 mb-1.5">Business email *</label>
          <input id="contact-email" data-testid="contact-form-input-email" name="email" type="email" required
            value={form.email} onChange={update} className={inputClass} placeholder="jane@yourbusiness.com.au" autoComplete="email" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700 mb-1.5">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
          <input id="contact-phone" data-testid="contact-form-input-phone" name="phone" type="tel" maxLength={40}
            value={form.phone} onChange={update} className={inputClass} placeholder="08 8XXX XXXX" autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="contact-company" className="block text-sm font-medium text-slate-700 mb-1.5">Business name</label>
          <input id="contact-company" data-testid="contact-form-input-company" name="business_name" type="text" maxLength={160}
            value={form.business_name} onChange={update} className={inputClass} placeholder="Your Business Pty Ltd" autoComplete="organization" />
        </div>
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700 mb-1.5">
          How can we help? {compact ? <span className="text-slate-400 font-normal">(optional)</span> : "*"}
        </label>
        <textarea id="contact-message" data-testid="contact-form-input-message" name="message" required={!compact} rows={compact ? 3 : 5} maxLength={5000}
          value={form.message} onChange={update} className={inputClass}
          placeholder="Tell us a little about your business and what's prompting you to look at IT support…" />
      </div>

      {/* Honeypot — invisible to humans */}
      <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor="contact-website">Website</label>
        <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={update} />
      </div>

      {status === "error" && (
        <div data-testid="contact-form-error-message" className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        type="submit"
        data-testid="contact-form-submit-button"
        disabled={status === "submitting"}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#17B8A6] hover:bg-[#0D9488] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-8 py-3.5 rounded-lg transition-colors duration-200 hover:-translate-y-0.5"
      >
        {status === "submitting" && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
        {status === "submitting" ? "Sending…" : "Request My Free Consultation"}
      </button>
      <p className="text-xs text-slate-500">No spam, no obligation, no lock-in contracts. Your details go straight to our Adelaide team — see our <a href="/privacy-policy" className="underline hover:text-[#0D9488]">Privacy Policy</a> for how we handle them.</p>
    </form>
  );
};

export default ContactForm;
